import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { identifyUser, resetUser, trackEvent } from '../services/analyticsService';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import {
  clearAllAuthTokens,
  clearImpersonationToken,
  getBaseAuthToken,
  getEffectiveAuthToken,
  getImpersonationToken,
  IMPERSONATION_RETURN_KEY,
  setBaseAuthToken,
  setImpersonationToken,
} from '../services/authToken';

interface JwtPayload {
  id: string;
  email: string;
  iat: number;
  exp: number;
  tokenType?: 'admin_impersonation';
  impersonationSessionId?: string;
  actorUserId?: string;
}

// Simple JWT decoder function to avoid import issues
const decodeJwt = (token: string): JwtPayload => {
  try {
    // JWT tokens are in format: header.payload.signature
    // We only need the payload part which is the second part
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    throw error;
  }
};

interface AuthContextType {
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  checkTokenExpiration: () => boolean; // New function to check token validity
  currentUser: CurrentUser | null;
  isUserLoading: boolean;
  impersonation: ImpersonationInfo | null;
  enterImpersonation: (token: string, targetId: string) => void;
  exitImpersonation: () => Promise<void>;
}

export interface ImpersonationInfo {
  sessionId: string;
  actor: { id: string; email: string };
  target: { id: string; email: string };
  expiresAt: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  role: 'user' | 'admin';
  accountStatus: 'active' | 'suspended';
  isVerified: boolean;
  authProvider: string;
  googleAvatarUrl: string | null;
  chefPanelVisible: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  impersonation?: ImpersonationInfo;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [token, setToken] = useState<string | null>(() => {
    // Initialize token from localStorage if it exists
    let storedToken = getEffectiveAuthToken();
    
    // Check if token exists and is valid
    if (storedToken) {
      try {
        const decodedToken = decodeJwt(storedToken);
        const currentTime = Date.now() / 1000;
        
        // If token is expired, clear it
        if (decodedToken.exp < currentTime) {
          console.log('Token expired on initialization, clearing token');
          if (getImpersonationToken()) {
            clearImpersonationToken();
            storedToken = getBaseAuthToken();
            if (storedToken && decodeJwt(storedToken).exp >= currentTime) return storedToken;
          }
          clearAllAuthTokens();
          return null;
        }
        
        return storedToken;
      } catch (error) {
        console.error('Error decoding token on initialization:', error);
        if (getImpersonationToken()) {
          clearImpersonationToken();
          const baseToken = getBaseAuthToken();
          try {
            if (baseToken && decodeJwt(baseToken).exp >= Date.now() / 1000) return baseToken;
          } catch {
            // The base token is also invalid and will be cleared below.
          }
        }
        clearAllAuthTokens();
        return null;
      }
    }
    
    return storedToken;
  });
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isUserLoading, setIsUserLoading] = useState(Boolean(token));

  useEffect(() => {
    if (!token) {
      setCurrentUser(null);
      setIsUserLoading(false);
      return;
    }
    let cancelled = false;
    let replacingToken = false;
    setIsUserLoading(true);
    fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async response => {
        if (!response.ok) throw Object.assign(new Error('Could not load account'), { status: response.status });
        return response.json();
      })
      .then(user => { if (!cancelled) setCurrentUser(user); })
      .catch(error => {
        if (cancelled) return;
        setCurrentUser(null);
        if (error?.status === 401 || error?.status === 403) {
          if (getImpersonationToken()) {
            const returnTo = clearImpersonationToken();
            const baseToken = getBaseAuthToken();
            replacingToken = true;
            setIsUserLoading(Boolean(baseToken));
            setToken(baseToken);
            navigate(returnTo, { replace: true });
          } else {
            clearAllAuthTokens();
            setToken(null);
          }
        }
      })
      .finally(() => { if (!cancelled && !replacingToken) setIsUserLoading(false); });
    return () => { cancelled = true; };
  }, [navigate, token]);

  const identifyTokenUser = (authToken: string) => {
    const decodedToken = decodeJwt(authToken);

    identifyUser(decodedToken.id, {
      email: decodedToken.email,
      $name: decodedToken.email
    });
  };

  // Identify an already-authenticated user immediately after an app reload.
  useEffect(() => {
    if (token) {
      try {
        if (decodeJwt(token).tokenType === 'admin_impersonation') return;
        identifyTokenUser(token);
      } catch (error) {
        console.error('Error identifying user in PostHog:', error);
      }
    }
  }, [token]);

  const login = (newToken: string) => {
    try {
      // Identify synchronously so the login event and the protected-page
      // navigation are associated with the user. PostHog also merges the
      // anonymous pre-auth activity and replay into this identity.
      identifyTokenUser(newToken);
    } catch (error) {
      console.error('Error identifying user in PostHog:', error);
    }

    clearImpersonationToken();
    setBaseAuthToken(newToken);
    setCurrentUser(null);
    setIsUserLoading(true);
    setToken(newToken);
  };

  const logout = useCallback(() => {
    // Track logout event before resetting user
    trackEvent('user_logout', {
      timestamp: new Date().toISOString()
    });
    
    // Reset the user in PostHog before clearing the token
    resetUser();
    const impersonationToken = getImpersonationToken();
    if (impersonationToken) {
      void fetch(`${API_BASE_URL}/auth/impersonation/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${impersonationToken}` },
        keepalive: true,
      }).catch(error => console.error('Could not notify the server that impersonation ended:', error));
    }
    clearAllAuthTokens();
    setCurrentUser(null);
    setToken(null);
  }, []);

  const enterImpersonation = (impersonationToken: string, targetId: string) => {
    setImpersonationToken(impersonationToken, targetId);
    setCurrentUser(null);
    setIsUserLoading(true);
    setToken(impersonationToken);
  };

  const exitImpersonation = useCallback(async () => {
    const activeToken = getImpersonationToken();
    const returnTo = sessionStorage.getItem(IMPERSONATION_RETURN_KEY) || '/admin/users';
    if (activeToken) {
      try {
        await fetch(`${API_BASE_URL}/auth/impersonation/end`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${activeToken}` },
        });
      } catch (error) {
        console.error('Could not notify the server that impersonation ended:', error);
      }
    }
    clearImpersonationToken();
    const baseToken = getBaseAuthToken();
    if (!baseToken) {
      setToken(null);
      navigate('/login', { state: { from: returnTo }, replace: true });
      return;
    }
    try {
      if (decodeJwt(baseToken).exp < Date.now() / 1000) throw new Error('Administrator token expired');
      setCurrentUser(null);
      setIsUserLoading(true);
      setToken(baseToken);
      navigate(returnTo, { replace: true });
    } catch {
      localStorage.removeItem('token');
      setToken(null);
      navigate('/login', { state: { from: returnTo }, replace: true });
    }
  }, [navigate]);

  // Function to check if token is expired
  const checkTokenExpiration = useCallback((): boolean => {
    if (!token) return false;
    
    try {
      const decodedToken = decodeJwt(token);
      const currentTime = Date.now() / 1000;
      
      // If token is expired
      if (decodedToken.exp < currentTime) {
        if (decodedToken.tokenType === 'admin_impersonation') {
          void exitImpersonation();
          return false;
        }
        console.log('Token expired, logging out user');
        // Force logout
        logout();
        
        // Redirect to login with return URL
        navigate('/login', { 
          state: { from: location.pathname },
          replace: true
        });
        
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      if (getImpersonationToken()) {
        void exitImpersonation();
        return false;
      }
      logout();
      
      // Redirect to login with return URL
      navigate('/login', { 
        state: { from: location.pathname },
        replace: true
      });
      
      return false;
    }
  }, [exitImpersonation, location.pathname, logout, navigate, token]);
  
  // Check token expiration periodically
  useEffect(() => {
    if (!token) return;
    
    // Set up interval to check token expiration
    const checkInterval = setInterval(() => {
      checkTokenExpiration();
    }, 60000); // Check every minute
    
    // Initial check
    checkTokenExpiration();
    
    return () => clearInterval(checkInterval);
  }, [checkTokenExpiration, token]);

  // The server can end a session before its JWT expires when another session
  // supersedes it or either account becomes ineligible. Check that durable
  // state periodically so direct-fetch screens recover without waiting for a
  // particular request path to handle a 401 response.
  useEffect(() => {
    if (!token || !currentUser?.impersonation) return;
    const checkServerSession = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.status !== 401 && response.status !== 403) return;
        const returnTo = clearImpersonationToken();
        const baseToken = getBaseAuthToken();
        setCurrentUser(null);
        setIsUserLoading(Boolean(baseToken));
        setToken(baseToken);
        navigate(baseToken ? returnTo : '/login', {
          state: baseToken ? undefined : { from: returnTo },
          replace: true,
        });
      } catch {
        // A temporary network failure must not discard either identity.
      }
    };
    const interval = window.setInterval(checkServerSession, 30_000);
    return () => window.clearInterval(interval);
  }, [currentUser?.impersonation, navigate, token]);

  const value = {
    token,
    login,
    logout,
    isAuthenticated: !!token,
    checkTokenExpiration,
    currentUser,
    isUserLoading,
    impersonation: currentUser?.impersonation || null,
    enterImpersonation,
    exitImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
